/**
 * 3D Planet — live personalisatie in 3D (V1).
 * Laadt het basis-GLB van het product (zonder tekst) in een three.js-viewer en plaatst de ingetypte
 * naam/tekst als reliëf (TextGeometry, extrusie 1,0 mm) op een per product ingesteld vlak.
 * Volgt het personalisatieveld (properties[Naam / tekst]) live en de kleur-swatch via `tdp-3d:color`.
 * Auto-rotatie stopt bij interactie; touch werkt via OrbitControls. Zonder WebGL blijft de sectie verborgen.
 *
 * Alle maten in de data-attributen zijn in mm; het GLB is in meters (Shopify/AR-schaal), dus we werken
 * intern in meters (MM = 0.001).
 */
const MM = 0.001;
/** Tekstkleur-optie (tweekleurig printen): naam -> filamentkleur. 'Zelfde als product' = volg de modelkleur. */
const TEXT_OPTION_NAME = 'Tekstkleur';
const TEXT_COLOR_HEX = { wit: '#f2f2f0', zwart: '#15161a', zilver: '#b9bcc2', grijs: '#7d8087', rood: '#c8232c', blauw: '#1f5fc4', groen: '#2e9e4a', geel: '#f2c318', oranje: '#f07a1e' };
function selectedTextColorHex() {
  const radio = document.querySelector(`input[type="radio"][data-option-name="${TEXT_OPTION_NAME}"]:checked`);
  const select = document.querySelector(`select[name="options[${TEXT_OPTION_NAME}]"]`);
  const name = (radio && radio.value) || (select && select.value) || '';
  return TEXT_COLOR_HEX[String(name).trim().toLowerCase()] || null;
}
const AUTO_ROTATE_SPEED = 0.9;
const TEXT_DEPTH_MM = 1.0;
const CAMERA_FOV = 32;
const MAX_TEXT_LENGTH = 40;
const COLOR_EVENT = 'tdp-3d:color';
const MOBILE_QUERY = '(max-width: 749px)';
const LAZY_ROOT_MARGIN = '320px 0px'; // three.js (612 KB) pas laden als de sectie bijna in beeld komt of het naamveld focus krijgt
const STAGE_INLINE_CLASS = 'tdp-3d-personalise__stage--inline';
const RELIEF_LIGHTNESS_SHIFT = 0.1; // reliëf in "dezelfde kleur" iets lichter/donkerder, zoals de randen van een echte print
const RELIEF_MIN_LIGHTNESS = 0.12; // op zwart (#111) blijft het reliëf anders onzichtbaar
const PLACEHOLDER_OPACITY = 0.55; // placeholder-reliëf ("Typ je naam") half doorzichtig, tot de klant typt
const PLACEHOLDER_LIGHTNESS_DARK = 0.62; // placeholder op een donker model: duidelijk lichter grijs
const PLACEHOLDER_LIGHTNESS_LIGHT = 0.42; // placeholder op een licht model: middengrijs
const TEXT_POP_MS = 320; // tekst "popt" kort op bij elke wijziging (schaal 0,7 → 1, ease-out)
const INTRO_DOLLY_MS = 900; // camera glijdt bij het laden van 1,45× naar 1× afstand
const MODEL_ROUGHNESS = 0.42; // lichte glans zoals een echte print onder daglicht
const MODEL_METALNESS = 0.06;

/** @param {string} hex */
function normaliseHex(hex) {
  const clean = String(hex || '').trim().replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  return /^[0-9a-f]{6}$/i.test(full) ? `#${full.toLowerCase()}` : null;
}

/** @param {string} value @param {number} fallback */
function num(value, fallback = 0) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function hasWebGL() {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

class TdpPersonalise3D extends HTMLElement {
  #three = null;
  #renderer = null;
  #scene = null;
  #camera = null;
  #controls = null;
  #model = null;
  #textMesh = null;
  #font = null;
  #introStart = 0;
  #baseDistance = 0;
  #popStart = 0;
  #textBaseScale = 1;
  #modelMaterials = [];
  #textMaterial = null;
  #placeholderMaterial = null;
  #anchor = null;
  #running = false;
  #visible = true;
  #abort = new AbortController();
  #resize = null;
  #intersection = null;
  #pendingText = '';
  #frame = 0;
  #stageHome = null;
  #mobileQuery = null;

  /** Tekstkleur-optie gewijzigd: tekstmateriaal direct bijwerken. */
  #onTextColorOption = (event) => {
    const t = event.target;
    const isRadio = t instanceof HTMLInputElement && t.dataset.optionName === TEXT_OPTION_NAME;
    const isSelect = t instanceof HTMLSelectElement && t.name === `options[${TEXT_OPTION_NAME}]`;
    if (!isRadio && !isSelect || !this.#textMaterial || !this.#three) return;
    const hex = selectedTextColorHex();
    this.#textMaterial.color.copy(hex ? new this.#three.Color(hex) : this.#reliefColor(new this.#three.Color(this.#anchor.modelHex)));
  };

  connectedCallback() {
    document.addEventListener('change', this.#onTextColorOption);
    if (!hasWebGL()) {
      this.hidden = true;
      return;
    }
    this.#anchor = this.#readAnchor();
    this.#pendingText = this.#sourceValue();
    this.#bindInputs();
    this.#startWhenNeeded();
  }

  /** Lazy start (reviewer r6 A#10): pas initialiseren bij zicht op de sectie of focus/invoer in het naamveld. */
  #startWhenNeeded() {
    let started = false;
    /** @type {IntersectionObserver | null} */
    let lazyObserver = null;
    const start = () => {
      if (started) return;
      started = true;
      lazyObserver?.disconnect();
      this.#init().catch((error) => {
        console.warn('[tdp-3d-personalise] viewer niet gestart', error);
        this.hidden = true;
      });
    };
    const { signal } = this.#abort;
    const source = this.#sourceInput;
    if (source instanceof HTMLInputElement) {
      source.addEventListener('focus', start, { signal, once: true });
      source.addEventListener('input', start, { signal, once: true });
    }
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    lazyObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) start();
    }, { rootMargin: LAZY_ROOT_MARGIN });
    lazyObserver.observe(this);
  }

  disconnectedCallback() {
    this.#running = false;
    cancelAnimationFrame(this.#frame);
    this.#abort.abort();
    this.#resize?.disconnect();
    this.#intersection?.disconnect();
    this.#controls?.dispose();
    this.#renderer?.dispose();
  }

  /* ---------- settings ---------- */

  #readAnchor() {
    const d = this.dataset;
    return {
      position: [num(d.posX), num(d.posY), num(d.posZ)],
      rotation: [num(d.rotX), num(d.rotY), num(d.rotZ)],
      maxWidth: num(d.maxWidth, 40),
      fontSize: num(d.fontSize, 8),
      depth: num(d.depth, TEXT_DEPTH_MM),
      elevation: num(d.cameraElevation, 35),
      azimuth: num(d.cameraAzimuth, 0),
      autoRotate: d.autoRotate !== 'false',
      textMode: d.textColorMode || 'same',
      contrastHex: normaliseHex(d.textContrastHex) || '#ffffff',
      modelHex: normaliseHex(d.modelHex) || '#111111',
      placeholder: String(d.placeholderText || '').trim(),
    };
  }

  get #sourceInput() {
    const selector = this.dataset.sourceSelector || '[data-tdp-personalisation-input]';
    return document.querySelector(selector);
  }

  #sourceValue() {
    const input = this.#sourceInput;
    return input instanceof HTMLInputElement ? input.value : '';
  }

  /* ---------- inputs ---------- */

  #bindInputs() {
    const { signal } = this.#abort;
    const mirror = this.querySelector('[data-tdp-3d-mirror]');
    const onInput = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      const isSource = target === this.#sourceInput || target.hasAttribute('data-tdp-personalisation-input');
      const isMirror = target === mirror;
      if (!isSource && !isMirror) return;
      const value = target.value;
      if (isMirror) {
        const source = this.#sourceInput;
        if (source instanceof HTMLInputElement && source.value !== value) {
          source.value = value;
          source.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } else if (mirror instanceof HTMLInputElement && mirror.value !== value) {
        mirror.value = value;
      }
      this.setText(value);
    };
    document.addEventListener('input', onInput, { signal });
    document.addEventListener(COLOR_EVENT, (event) => {
      const hex = normaliseHex(event.detail?.hex);
      if (hex) this.setModelColor(hex);
    }, { signal });
    if (mirror instanceof HTMLInputElement) mirror.value = this.#pendingText;
  }

  /* ---------- three.js ---------- */

  async #init() {
    const THREE = await import(new URL(this.dataset.threeSrc, document.baseURI).href);
    this.#three = THREE;
    const stage = this.querySelector('[data-tdp-3d-stage]');
    if (!stage) throw new Error('stage ontbreekt');

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    stage.appendChild(renderer.domElement);
    renderer.domElement.setAttribute('aria-label', this.dataset.canvasLabel || '3D-voorbeeld van je personalisatie');
    renderer.domElement.setAttribute('role', 'img');
    this.#renderer = renderer;

    const scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xffffff, 0x3a4450, 1.8));
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(-0.4, 0.8, 0.6);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xdfe8ff, 0.9);
    fill.position.set(0.6, 0.3, -0.5);
    scene.add(fill);
    const graze = new THREE.DirectionalLight(0xffffff, 1.4); // laag strijklicht: zijkanten van het reliëf lichten op
    graze.position.set(0.9, 0.15, 0.7);
    scene.add(graze);
    // Randlicht van achteren/boven: een zwart model tekent zo zijn silhouet af tegen de donkere achtergrond.
    const rim = new THREE.DirectionalLight(0xdfe8ff, 2.4);
    rim.position.set(0.2, 0.9, -1);
    scene.add(rim);
    const rimLow = new THREE.DirectionalLight(0xbfd0e0, 1.2);
    rimLow.position.set(-0.8, -0.3, -0.6);
    scene.add(rimLow);
    this.#scene = scene;

    const camera = new THREE.PerspectiveCamera(CAMERA_FOV, 1, 0.001, 10);
    this.#camera = camera;

    const controls = new THREE.OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = false;
    controls.autoRotate = this.#anchor.autoRotate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    controls.autoRotateSpeed = AUTO_ROTATE_SPEED;
    controls.addEventListener('start', () => {
      controls.autoRotate = false;
      this.classList.add('tdp-3d-personalise--touched');
    });
    this.#controls = controls;

    await Promise.all([this.#loadModel(), this.#loadFont()]);
    this.#frameModel();
    this.setText(this.#pendingText);
    this.#observe(stage);
    this.#bindPlacement(stage);
    this.setAttribute('data-ready', '');
    this.#running = true;
    this.#loop();
  }

  async #loadModel() {
    const THREE = this.#three;
    const loader = new THREE.GLTFLoader();
    const gltf = await loader.loadAsync(new URL(this.dataset.modelSrc, document.baseURI).href);
    const model = gltf.scene;
    this.#modelMaterials = [];
    model.traverse((node) => {
      if (node.isMesh && node.material) {
        if ('roughness' in node.material) { node.material.roughness = MODEL_ROUGHNESS; node.material.metalness = MODEL_METALNESS; }
        this.#modelMaterials.push(node.material);
      }
    });
    this.#scene.add(model);
    this.#model = model;
    this.setModelColor(this.#anchor.modelHex);
  }

  async #loadFont() {
    const THREE = this.#three;
    const loader = new THREE.FontLoader();
    this.#font = await loader.loadAsync(new URL(this.dataset.fontSrc, document.baseURI).href);
  }

  #frameModel() {
    const THREE = this.#three;
    const box = new THREE.Box3().setFromObject(this.#model);
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const centre = sphere.center;
    const distance = (sphere.radius * 1.15) / Math.sin(THREE.MathUtils.degToRad(CAMERA_FOV / 2));
    const elevation = THREE.MathUtils.degToRad(this.#anchor.elevation);
    const azimuth = THREE.MathUtils.degToRad(this.#anchor.azimuth);
    this.#camera.position.set(
      centre.x + distance * Math.cos(elevation) * Math.sin(azimuth),
      centre.y + distance * Math.sin(elevation),
      centre.z + distance * Math.cos(elevation) * Math.cos(azimuth)
    );
    this.#camera.near = distance / 100;
    this.#camera.far = distance * 20;
    this.#camera.updateProjectionMatrix();
    this.#controls.target.copy(centre);
    this.#controls.minDistance = distance * 0.5;
    this.#controls.maxDistance = distance * 2.5;
    this.#baseDistance = distance;
    this.#introStart = performance.now();
    this.#controls.update();
  }

  #observe(stage) {
    this.#resize = new ResizeObserver(() => this.#fit(stage));
    this.#resize.observe(stage);
    this.#fit(stage);
    this.#intersection = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      if (this.#visible && this.#running) this.#loop();
    });
    this.#intersection.observe(stage);
  }

  /**
   * Mobiel: de preview staat direct onder het koopblok-invoerveld (het element tdp-personalisation),
   * desktop: in de eigen sectie. Het canvas verhuist mee; de WebGL-context blijft bestaan.
   * @param {HTMLElement} stage
   */
  #bindPlacement(stage) {
    this.#stageHome = document.createComment('tdp-3d-stage');
    stage.before(this.#stageHome);
    this.#mobileQuery = window.matchMedia(MOBILE_QUERY);
    const place = () => {
      const source = this.#sourceInput;
      const target = source?.closest('tdp-personalisation') || source?.parentElement;
      if (this.#mobileQuery.matches && target && !target.contains(stage)) {
        target.after(stage);
        stage.classList.add(STAGE_INLINE_CLASS);
      } else if (!this.#mobileQuery.matches && this.#stageHome?.parentNode && stage.previousSibling !== this.#stageHome) {
        this.#stageHome.after(stage);
        stage.classList.remove(STAGE_INLINE_CLASS);
      }
      this.#fit(stage);
    };
    this.#mobileQuery.addEventListener('change', place, { signal: this.#abort.signal });
    place();
  }

  #fit(stage) {
    const width = Math.max(stage.clientWidth, 1);
    const height = Math.max(stage.clientHeight, 1);
    this.#renderer.setSize(width, height, false);
    this.#camera.aspect = width / height;
    this.#camera.updateProjectionMatrix();
  }

  #loop() {
    cancelAnimationFrame(this.#frame);
    if (!this.#running || !this.#visible) return;
    const now = performance.now();
    // Intro: camera glijdt naar binnen (ease-out) zodra het model geladen is.
    if (this.#introStart && this.#baseDistance) {
      const t = Math.min(1, (now - this.#introStart) / INTRO_DOLLY_MS);
      const ease = 1 - (1 - t) ** 3;
      const distance = this.#baseDistance * (1.45 - 0.45 * ease);
      const direction = this.#camera.position.clone().sub(this.#controls.target).normalize();
      this.#camera.position.copy(this.#controls.target).addScaledVector(direction, distance);
      if (t >= 1) this.#introStart = 0;
    }
    // Pop: nieuwe tekst schaalt kort van 0,7 naar 1.
    if (this.#textMesh && this.#popStart) {
      const t = Math.min(1, (now - this.#popStart) / TEXT_POP_MS);
      const ease = 1 - (1 - t) ** 3;
      this.#textMesh.scale.setScalar(this.#textBaseScale * (0.7 + 0.3 * ease));
      if (t >= 1) this.#popStart = 0;
    }
    this.#controls.update();
    this.#renderer.render(this.#scene, this.#camera);
    this.#frame = requestAnimationFrame(() => this.#loop());
  }

  /* ---------- public API ---------- */

  /** Alleen-lezen status (QA/e2e): geladen model, tekstmesh en afmetingen in mm. */
  get state() {
    const THREE = this.#three;
    const box = this.#textMesh && THREE ? new THREE.Box3().setFromObject(this.#textMesh) : null;
    return {
      ready: this.hasAttribute('data-ready'),
      text: this.#pendingText,
      hasTextMesh: Boolean(this.#textMesh),
      textSizeMm: box ? [box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z].map((v) => Math.round(v / MM * 10) / 10) : null,
      textCentreMm: box ? [box.min.x + box.max.x, box.min.y + box.max.y, box.min.z + box.max.z].map((v) => Math.round(v / MM * 5) / 10) : null,
      modelHex: this.#anchor?.modelHex,
      materials: this.#modelMaterials.length,
    };
  }

  /** @param {string} hex */
  setModelColor(hex) {
    const THREE = this.#three;
    const clean = normaliseHex(hex);
    if (!THREE || !clean) return;
    this.#anchor.modelHex = clean;
    const color = new THREE.Color(clean);
    this.#modelMaterials.forEach((material) => {
      material.color.copy(color);
      material.roughness = 0.55;
      material.metallic = 0;
      material.needsUpdate = true;
    });
    if (this.#textMaterial && this.#anchor.textMode === 'same') {
      const chosen = selectedTextColorHex();
      this.#textMaterial.color.copy(chosen ? new THREE.Color(chosen) : this.#reliefColor(color));
    }
    if (this.#placeholderMaterial) {
      this.#placeholderMaterial.color.copy(this.#placeholderColor(color));
    }
  }

  /** Zelfde tint als het model, maar net licht genoeg om het reliëf te zien. @param {any} color */
  #reliefColor(color) {
    const THREE = this.#three;
    const hsl = color.getHSL({ h: 0, s: 0, l: 0 });
    const lightness = hsl.l < 0.5 ? hsl.l + RELIEF_LIGHTNESS_SHIFT : hsl.l - RELIEF_LIGHTNESS_SHIFT;
    return new THREE.Color().setHSL(hsl.h, hsl.s, Math.min(1, Math.max(RELIEF_MIN_LIGHTNESS, lightness)));
  }

  /** Placeholder-tint: vaste lichtheid tegenover het model, zodat "Typ je naam" op zwart én wit leesbaar is. @param {any} color */
  #placeholderColor(color) {
    const THREE = this.#three;
    const hsl = color.getHSL({ h: 0, s: 0, l: 0 });
    const lightness = hsl.l < 0.5 ? PLACEHOLDER_LIGHTNESS_DARK : PLACEHOLDER_LIGHTNESS_LIGHT;
    return new THREE.Color().setHSL(hsl.h, Math.min(hsl.s, 0.3), lightness);
  }

  /** @param {string} value */
  setText(value) {
    this.#pendingText = String(value ?? '').slice(0, MAX_TEXT_LENGTH);
    const THREE = this.#three;
    if (!THREE || !this.#font || !this.#scene) return;
    if (this.#textMesh) {
      this.#scene.remove(this.#textMesh);
      this.#textMesh.geometry.dispose();
      this.#textMesh = null;
    }
    const typed = this.#pendingText.trim();
    this.toggleAttribute('data-empty', typed.length === 0);
    // Lege invoer: toon de placeholder als half doorzichtig reliëf, zodat het tekstvlak zichtbaar blijft.
    const isPlaceholder = typed.length === 0;
    const text = isPlaceholder ? this.#anchor.placeholder : typed;
    if (!text) return;

    const { position, rotation, maxWidth, fontSize, depth } = this.#anchor;
    const geometry = new THREE.TextGeometry(text, {
      font: this.#font,
      size: fontSize * MM,
      depth: depth * MM,
      curveSegments: 6,
      bevelEnabled: true,
      bevelThickness: 0.12 * MM,
      bevelSize: 0.08 * MM,
      bevelSegments: 2,
    });
    geometry.computeBoundingBox();
    const bounds = geometry.boundingBox;
    const width = bounds.max.x - bounds.min.x;
    const height = bounds.max.y - bounds.min.y;
    const scale = Math.min(1, (maxWidth * MM) / Math.max(width, 1e-6));
    // centreer op het ankerpunt (x en y van de tekst), reliëf steekt vanaf z=0 naar buiten
    geometry.translate(-(bounds.min.x + width / 2), -(bounds.min.y + height / 2), 0);

    if (!this.#textMaterial) {
      this.#textMaterial = new THREE.MeshStandardMaterial({ roughness: 0.55, metalness: 0 });
    }
    if (!this.#placeholderMaterial) {
      this.#placeholderMaterial = new THREE.MeshStandardMaterial({
        roughness: 0.55,
        metalness: 0,
        transparent: true,
        opacity: PLACEHOLDER_OPACITY,
      });
    }
    const chosenTextHex = selectedTextColorHex();
    const reliefColor = chosenTextHex
      ? new THREE.Color(chosenTextHex)
      : this.#anchor.textMode === 'contrast'
        ? new THREE.Color(this.#anchor.contrastHex)
        : this.#reliefColor(new THREE.Color(this.#anchor.modelHex));
    this.#textMaterial.color.copy(reliefColor);
    // Placeholder in een vaste contrasttint tegenover de modelkleur, anders valt hij op zwart weg.
    this.#placeholderMaterial.color.copy(this.#placeholderColor(new THREE.Color(this.#anchor.modelHex)));

    const mesh = new THREE.Mesh(geometry, isPlaceholder ? this.#placeholderMaterial : this.#textMaterial);
    mesh.scale.setScalar(scale);
    this.#textBaseScale = scale;
    if (!isPlaceholder) this.#popStart = performance.now();
    mesh.position.set(position[0] * MM, position[1] * MM, position[2] * MM);
    mesh.rotation.set(
      THREE.MathUtils.degToRad(rotation[0]),
      THREE.MathUtils.degToRad(rotation[1]),
      THREE.MathUtils.degToRad(rotation[2])
    );
    this.#scene.add(mesh);
    this.#textMesh = mesh;
  }
}

if (!customElements.get('tdp-3d-personalise')) {
  customElements.define('tdp-3d-personalise', TdpPersonalise3D);
}
